import { useMutation } from 'react-query';

import { putV2ApiCompanyIdAccountingIntegrationsMappingAnalyticalFieldsFieldId } from 'src/client/spendesk-api';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

import { useInvalidateSyncOperationQuery } from './useGetSyncOperation';
import { useInvalidateSyncSettingsQuery } from './useGetSyncSettings';
import {
  type AnalyticalFieldMappingV2,
  type AnalyticalFieldMappingFieldType,
} from '../../bookkeep/settings/accounting';
import { useInvalidateAnalyticalFieldMappingQuery } from '../../bookkeep/settings/integrations/hooks/useAnalyticalFieldMappingsQuery';

export type MappingUpdate = {
  fieldId: string;
  fieldType: AnalyticalFieldMappingFieldType;
  internalId: string | null;
  values?: { id: string; internalId: string | null }[];
};

export const useUpdateAnalyticalFieldMapping = (
  onDone?: (field: AnalyticalFieldMappingV2) => void,
) => {
  const invalidateFieldMapping = useInvalidateAnalyticalFieldMappingQuery();
  const invalidateSyncSettings = useInvalidateSyncSettingsQuery();
  const invalidateSyncOperationQuery = useInvalidateSyncOperationQuery();
  const companyId = useCompanyId();

  const {
    isLoading,
    data,
    mutate: updateAnalyticalFieldMapping,
  } = useMutation({
    mutationFn: async ({ fieldId, ...update }: MappingUpdate) => {
      const { statusCode, body } =
        await putV2ApiCompanyIdAccountingIntegrationsMappingAnalyticalFieldsFieldId(
          {
            path: {
              companyId,
              fieldId,
            },
            body: update,
          },
        );

      if (statusCode !== 200) {
        throw new Error('Failed to update analytical field mappings');
      }

      return body;
    },
    onSuccess: (response) => {
      onDone?.(response);
      invalidateFieldMapping();
      invalidateSyncSettings();
      invalidateSyncOperationQuery();
    },
  });

  return {
    isPending: isLoading,
    fieldMapping: data,
    updateAnalyticalFieldMapping,
  };
};
